/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { Grid } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import clsx from 'clsx';
import { Trans, useTranslation } from 'react-i18next';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import ToggleButton from '@material-ui/lab/ToggleButton';
import Icon from '@material-ui/core/Icon';
import Typography from '@material-ui/core/Typography';
import { get } from 'lodash';
import DOMPurify from 'dompurify';
import CardsContainer from './parties/CardsContainer';
import Button from '../../common/Button';
import Tooltip from '../../common/Tooltip';
import AddContactButtonField from './AddContactButtonField';
import ListContainer from './parties/ListContainer';
import useFormEntitiesArrayTemplate, { LAYOUT } from './useFormEntitiesArrayTemplate';
import { isFeatureEnabled, ToggleableFeature } from '../../../shared/utils/featuresUtils';
import { useConfiguration } from '../../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../../redux/enums/ConfigResourceProps';
import { useBioMaterials } from '../../../redux/features/bioMaterials/useBioMaterials';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import ConfirmationDialog from './confirmation/ConfirmationDialog';
import { translateFallback } from '../../../shared/utils/i18nUtils';

const useStyles = makeStyles(theme => ({
  root: {
    margin: '0 0 2rem 0',
  },
  itemsNumber: {
    marginLeft: '0.25rem',
    color: theme.palette.input,
  },
  paper: {
    margin: '1.5rem 0',
    padding: '1rem 1rem',
    backgroundColor: theme.palette.elementBackground,
    boxShadow: 'none',
    borderRadius: '1rem',

    [theme.breakpoints.up('md')]: {
      padding: '2rem 3rem',
    },
    [theme.breakpoints.up('xl2')]: {
      width: '75%',
      maxWidth: '60rem',
    },
  },
  buttonsDivider: {
    marginTop: '3rem',
    marginBottom: '2rem',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& > button:not(:first-child)': {
      marginLeft: '1.5rem',
    },
  },
  cardsContainer: {
    marginTop: '2rem',
  },
  cardsCommunicationAddressContainer: {
    marginTop: '-1rem',
  },
  alertInfoContainer: {
    marginTop: '2rem',
    backgroundColor: theme.palette.elementBackground,
  },
  toggleContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  addButton: {
    marginRight: 'auto',
  },
  dialogContent: {
    paddingLeft: '1rem',
    paddingRight: '1rem',
  },
  dialogTitle: {
    fontWeight: 600,
    fontSize: '1.9rem',
  },
}));

const useLabStyles = makeStyles({
  root: {},
});

/**
 * Template component responsible for rendering the form array entities.
 * It is composed of the array title area, card / list toggle button, the forms of the not confirmed array elements,
 * the cards or list items of the confirmed elements and a info / error message area.
 */
function FormEntitiesArrayTemplate(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const classesLab = useLabStyles();
  const { idSchema, disabled: formDisabled, formContext, registry } = props;
  const { submissionId } = formContext;
  const globalConfiguration = useConfiguration(ConfigResourceProps.GLOBAL_CONFIGURATION);

  const {
    confirmedEntities,
    notConfirmedEntities,
    onAdd,
    onEdit,
    onDelete,
    onReorder,
    onConfirm,
    onCancel,
    popupConfig,
    isDialogCardOpen,
    confirmDialogCard,
    closeDialogCard,
    layout,
    setLayout,
    isMediumOrBigger,
    uiSchema,
    title,
    btnAddId,
    summarySectionId,
    isEditingEntity,
  } = useFormEntitiesArrayTemplate(props);

  const infoMessage = uiSchema[uiSchemaProperties.UI_INFO_MESSAGE];
  const errorMessage = uiSchema[uiSchemaProperties.UI_ERROR_MESSAGE];
  const hideArrayTitle = uiSchema[uiSchemaProperties.UI_HIDE_ARRAY_TITLE];

  const { canAdd = true, canEdit = true, showInfoMessage = false, showErrorMessage = false } = uiSchema;

  // obtain the singular and plural entity labels
  const titleSingular = get(uiSchema, uiSchemaProperties.UI_TITLE_SINGULAR_ADD);
  const entityType = title.substr(0, title.length - 1).toLowerCase();
  const labelSingular = titleSingular || entityType;
  const labelPlural = title;

  const tooltipArray = uiSchema[uiSchemaProperties.UI_TOOLTIP];
  const numberOfCards = confirmedEntities ? confirmedEntities.length : 0;
  const summaryItemConfig = get(uiSchema, uiSchemaProperties.UI_SUMMARY_ITEM);

  // entity buttons configurations
  const hideAddButton = get(uiSchema, `${uiSchemaProperties.UI_HIDE_ADD_BUTTON}`);
  const hideAddAddressBookButton = get(uiSchema, `${uiSchemaProperties.UI_HIDE_ADD_ADDRESS_BOOK_BUTTON}`);
  const hideDeleteButton = get(uiSchema, `${uiSchemaProperties.UI_HIDE_DELETE_BUTTON}`);
  const disableConfirmButton = get(uiSchema, `${uiSchemaProperties.CONFIRM_BUTTON}.${uiSchemaProperties.UI_DISABLED}`);
  const hideLayoutSwitcher = get(uiSchema, `${uiSchemaProperties.UI_HIDE_LAYOUT_SWITCHER}`);
  const disableDragAndDrop = get(uiSchema, `${uiSchemaProperties.UI_DISABLE_DRAG_AND_DROP}`);

  const addButtonLabel = translateFallback(
    t,
    `action.add.${labelSingular.toLowerCase()}`,
    () => `${t('actions.add')} ${t(labelSingular.toLowerCase())}`,
  );

  const confirmButtonLabel = translateFallback(
    t,
    `action.confirm.${labelSingular.toLowerCase()}`,
    () => `${t('actions.confirm')} ${t(labelSingular.toLowerCase())}`,
  );

  const { onEditAction, onCancelAction, onDeleteAction, onConfirmAction } = useBioMaterials({
    registry,
    submissionId,
    idSchema,
    onCancel,
    onEdit,
    confirmDialogCard,
    onDelete,
    closeDialogCard,
    onConfirm,
  });

  const dialogProps = {
    showPopup: isDialogCardOpen,
    onHandleConfirm: confirmDialogCard,
    onHandleCancel: closeDialogCard,
    popupConfig,
  };

  return (
    <div className={clsx(props.className, classes.root, classes.wrapIcon)}>
      {!hideArrayTitle && (
        <Grid container direction="row" alignItems="center" id={summarySectionId}>
          <Typography variant="h4" component="h3" aria-label={`${labelPlural} (${numberOfCards})`}>
            <span>{t(labelPlural)}</span>
            <span className={classes.itemsNumber}>{` (${numberOfCards})`}</span>
          </Typography>
          {tooltipArray && <Tooltip title={<Trans i18nKey={tooltipArray} />} />}
        </Grid>
      )}

      <div className={classes.toggleContainer}>
        {/* ADD BUTTON */}
        {!formDisabled && !hideAddButton && (
          <Button
            id={btnAddId}
            disabled={!canAdd || isEditingEntity}
            onClick={onAdd}
            color="default"
            startIcon={<AddCircleOutlineIcon />}
            className={classes.addButton}
          >
            {addButtonLabel}
          </Button>
        )}

        {/* TO-DO Extract on own common component */}
        {/* GRID/LIST VIEW TOGGLE */}
        {!hideLayoutSwitcher && isMediumOrBigger && confirmedEntities.length > 0 && (
          <ToggleButtonGroup key="toggleButton" value={layout} classes={classesLab}>
            <ToggleButton value={LAYOUT.GRID} onClick={() => setLayout(LAYOUT.GRID)} classes={classesLab}>
              <Icon>apps</Icon>
              &nbsp; 
              {' '}
              {t('actions.view.grid')}
            </ToggleButton>
            <ToggleButton value={LAYOUT.LIST} onClick={() => setLayout(LAYOUT.LIST)} classes={classesLab}>
              <Icon>list</Icon>
              &nbsp; 
              {' '}
              {t('actions.view.list')}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </div>

      {/* FORM */}
      {notConfirmedEntities &&
        notConfirmedEntities.map(element => (
          <div key={element.key} className={classes.paper}>
            {element.children}
            <Divider className={classes.buttonsDivider} />
            <div className={classes.buttons}>
              {element?.children?.props?.idSchema?.$id.includes('parties') &&
                !hideAddAddressBookButton &&
                isFeatureEnabled(globalConfiguration.resource, ToggleableFeature.ADDRESS_BOOK) && (
                  <AddContactButtonField
                    formData={element?.children?.props?.formData}
                    onConfirm={onConfirmAction}
                    element={element}
                    id={`${summarySectionId}-addContact`}
                  />
                )}
              <Button onClick={() => onCancelAction(element)} color="default" id={`${summarySectionId}-cancel`}>
                {t('actions.cancel')}
              </Button>
              <Button
                onClick={() => onConfirmAction(element)}
                color="primary"
                disabled={disableConfirmButton}
                id={`${summarySectionId}-confirm`}
              >
                {confirmButtonLabel}
              </Button>
            </div>
          </div>
        ))}

      {/* CARD/LIST VIEW */}
      <section aria-label={`${t('Confirmed')} ${labelPlural}`}>
        {layout === LAYOUT.GRID && (
          <CardsContainer
            className={hideAddButton ? classes.cardsCommunicationAddressContainer : classes.cardsContainer}
            formEntities={confirmedEntities}
            config={summaryItemConfig}
            openDeleteDialog={!hideDeleteButton ? id => onDeleteAction(id) : null}
            openEditDialog={canEdit ? id => onEditAction(id) : null}
            readOnly={formDisabled}
            id={idSchema.$id}
            schema={formContext.schema}
            formContext={formContext}
          />
        )}
        {layout === LAYOUT.LIST && (
          <ListContainer
            className={classes.cardsContainer}
            formEntities={confirmedEntities}
            config={summaryItemConfig}
            openDeleteDialog={!hideDeleteButton ? id => onDeleteAction(id) : null}
            updateEntityOrder={onReorder}
            openEditDialog={canEdit ? id => onEditAction(id) : null}
            readOnly={formDisabled}
            schemaId={idSchema.$id}
            schema={formContext.schema}
            formContext={formContext}
            disableDragAndDrop={disableDragAndDrop}
          />
        )}
        {' '}
      </section>

      {popupConfig && <ConfirmationDialog {...dialogProps} />}

      {(showInfoMessage || showErrorMessage) && (
        <div id="info">
          {showInfoMessage && (
            <Alert id="alert-info" variant="outlined" severity="info" className={classes.alertInfoContainer}>
              <Trans i18nKey={infoMessage} />
            </Alert>
          )}
          {showErrorMessage && (
            <Alert id="alert-error" severity="error" className={classes.alertInfoContainer}>
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t(errorMessage)) }} />
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}

export default FormEntitiesArrayTemplate;
